import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { DirectiveSuperclass } from '@sama/ng-core';
import { clamp } from 'lodash';
import { from, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ToggleButton } from '../../../../../../../cross-team-shared/components/toggle-button-group/toggle-button-group.component';
import {
  defaultCloudDensity,
  EnhancedCloudSettingsState,
  EnhancedPointCloudDirection,
} from '../../../../../../../cross-team-shared/state/enhanced-point-cloud-setting-state';
import { settingsStoreToken } from '../../../../../../../cross-team-shared/state/setting-state';
import { SettingsService } from '../../../../../../h3ml/dsl/sensor-fusion/view-settings/settings.service';
import { TaskWorkspaceStore } from '../../../../../../h3ml/state/task-workspace-store';
import { FusionManager } from '../../../fusion/fusion-manager';
import { FusionManagerFactory } from '../../../fusion/fusion-manager-factory.service';

const maxAllowedFrameCount = 100;
const defaultFrameCount = 10;

@Component({
  selector: 'h3-enhanced-point-cloud-settings',
  templateUrl: './enhanced-point-cloud-settings.component.html',
  styleUrls: ['./enhanced-point-cloud-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EnhancedPointCloudSettingsComponent
  extends DirectiveSuperclass
  implements OnInit
{
  enhancedCloudStore!: Store<EnhancedCloudSettingsState>;
  enhancedPointCloudEnabled$!: Observable<boolean>;
  maxFrameCount$!: Observable<number>;
  defaultFrameCount$!: Observable<number>;
  defaultEnhancedPointDensity = defaultCloudDensity;
  directionButtons: Array<ToggleButton<EnhancedPointCloudDirection>> = [
    {
      label: 'Past',
      value: 'past',
      contextualClassWhenActive: 'primary',
    },
    {
      label: 'Future',
      value: 'future',
      contextualClassWhenActive: 'primary',
    },
    {
      label: 'All',
      value: 'all',
      contextualClassWhenActive: 'primary',
    },
  ];

  fusionManager!: FusionManager;
  settingsStore = inject(settingsStoreToken);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private settingsService = inject(SettingsService);
  private fusionManagerFactory = inject(FusionManagerFactory);

  ngOnInit() {
    this.enhancedCloudStore = this.settingsStore('enhancedCloudSettings');
    this.enhancedPointCloudEnabled$ = this.enhancedCloudStore('enabled').$;
    this.fusionManager = this.fusionManagerFactory.getManagers()[0];
    this.subscribeToSequenceLength();
  }

  toggleEnhancedPointCloud() {
    this.settingsService.toggleEnhancedPointCloud({ source: 'button' });
  }

  private subscribeToSequenceLength() {
    this.subscribeTo(
      from(this.fusionManager.sleepUntilPrimaryIsAvailable()).pipe(
        switchMap(() =>
          this.taskWorkspaceStore.getMaxFrameNumber$(
            this.fusionManager.getPrimaryMinion().output.name,
          ),
        ),
      ),
      (maxFrameNumber) => {
        const sequenceLength = maxFrameNumber + 1;
        const maxFrameCount = clamp(sequenceLength, 1, maxAllowedFrameCount);
        const frameCount = Math.min(maxFrameCount, defaultFrameCount);
        this.enhancedCloudStore('frames').set(frameCount);
        this.defaultFrameCount$ = of(frameCount);
        this.maxFrameCount$ = of(maxFrameCount);
      },
    );
  }
}
